import type { SupportResponse } from '../types'
import type { InfographicStyle } from '../stores/infographicStore'

// Cloudflare Worker endpoint (relative URL - same origin)
const API_URL = '/api/support'

export interface ApiError {
  message: string
  status?: number
}

export interface SubmitQueryOptions {
  question: string
  infographicStyle?: InfographicStyle
}

export async function submitQuery(
  questionOrOptions: string | SubmitQueryOptions
): Promise<SupportResponse> {
  // Support both old string API and new options API
  const options = typeof questionOrOptions === 'string'
    ? { question: questionOrOptions }
    : questionOrOptions

  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
  })

  if (!response.ok) {
    const error: ApiError = {
      message: `API Error: ${response.status} ${response.statusText}`,
      status: response.status,
    }
    throw error
  }

  const data = await response.json()

  // Validate response structure
  if (!data.tweet || !data.facebook || !data.forum) {
    console.error('Invalid API response:', data)
    throw { message: 'Invalid response format from API' } as ApiError
  }

  // Parse infographic data with fallbacks
  const infographic = data.infographic || {}

  // Debug: log infographic data
  console.log('Infographic data from API:', infographic)
  console.log('imageBase64 present:', !!infographic.imageBase64)
  console.log('imageBase64 length:', infographic.imageBase64?.length)

  return {
    tweet: data.tweet || '',
    facebook: data.facebook || '',
    forum: data.forum || '',
    infographic: {
      // New format (generated image)
      prompt: infographic.prompt,
      imageBase64: infographic.imageBase64,
      mimeType: infographic.mimeType,
      // Old format (concept card) - fallbacks
      title: infographic.title || 'Support Information',
      keyVisuals: infographic.keyVisuals || [],
      primaryTakeaway: infographic.primaryTakeaway || '',
      cta: infographic.cta || 'Visit canon.us/account for support',
    },
    sources: data.sources || [],
    confidence: data.confidence || 'medium',
    confidenceRationale: data.confidenceRationale || '',
    clarifyingQuestions: data.clarifyingQuestions || [],
  }
}
